import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, siteInfo } from '@/mocks/anime';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.REACT_APP_NAVIGATE(`/category/rexue?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  }, [searchQuery]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <div className="w-9 h-9 flex items-center justify-center">
              <i className="ri-chat-smile-2-line text-2xl text-primary-500"></i>
            </div>
            <span className="text-xl font-heading text-primary-600 font-bold tracking-wide">
              {siteInfo.name}
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                location.pathname === '/'
                  ? 'text-primary-500'
                  : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              首页
            </Link>
            <div className="relative group">
              <span className="text-sm font-medium whitespace-nowrap text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer flex items-center gap-1">
                分类浏览
                <i className="ri-arrow-down-s-line text-xs"></i>
              </span>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-background-50 rounded-lg border border-background-200/70 py-2 px-1 min-w-[180px]">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-xs`}></i>
                      </div>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <Link
              to="/about"
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                location.pathname === '/about'
                  ? 'text-primary-500'
                  : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              关于我们
            </Link>
          </div>

          {/* Desktop Right Actions */}
          <div className="hidden md:flex items-center gap-4">
            {isSearchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索动漫..."
                  className="w-48 px-3 py-1.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 bg-background-50 text-foreground-950"
                  autoFocus
                />
                <button type="submit" className="ml-2 p-1.5 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-search-line"></i>
                  </div>
                </button>
              </form>
            ) : (
              <button
                onClick={() => setIsSearchOpen(true)}
                className="p-2 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                aria-label="搜索"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-search-line text-lg"></i>
                </div>
              </button>
            )}
          </div>

          {/* Mobile Toggle */}
          <button
            className="md:hidden p-2 text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="菜单"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 py-4 bg-background-50 animate-in">
            <div className="flex flex-col gap-3">
              <Link
                to="/"
                className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-md cursor-pointer"
              >
                首页
              </Link>
              <div className="px-4 py-1">
                <p className="text-xs text-foreground-500 mb-2 font-medium">分类浏览</p>
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${cat.icon} text-xs`}></i>
                      </div>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
              <Link
                to="/about"
                className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-md cursor-pointer"
              >
                关于我们
              </Link>
              <form onSubmit={handleSearch} className="px-4 flex items-center gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索动漫..."
                  className="flex-1 px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 bg-background-50 text-foreground-950"
                />
                <button type="submit" className="p-2 bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-search-line"></i>
                  </div>
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}